import { App } from './App.js';
import { Forgot } from './features/account/Forgot.js';
import { Login } from './features/account/Login.js';
import { Begin } from './features/checkout/Begin.js';
import { Complete } from './features/checkout/Complete.js';
import { currentSession } from './lib/session.js';

/**
 * The portal's routing, kept to what the funnel needs and no framework.
 *
 * A single-page app served from CloudFront falls every unknown path back to `index.html` (the
 * distribution is configured that way), so the path is read here and the right screen is shown.
 * Three routes today:
 *
 *   /begin?plan=…                 the start of a purchase
 *   /checkout/complete?purchase=… the return from Stripe
 *   everything else               the reflective exercise
 *
 * When there are more, this becomes a real router. There is no reason to carry one for three paths.
 */
export function Root() {
  const { pathname, searchParams } = new URL(window.location.href);

  if (pathname === '/begin') {
    const plan = searchParams.get('plan');
    return plan ? <Begin planSlug={plan} /> : <MissingPlan />;
  }

  if (pathname === '/checkout/complete') {
    const purchase = searchParams.get('purchase');
    return purchase ? <Complete purchaseId={purchase} /> : <MissingPlan />;
  }

  if (pathname === '/login') {
    // Already signed in? There is nothing to do on the sign-in page — go to the reflection.
    return currentSession() ? redirectHome() : <Login />;
  }

  if (pathname === '/forgot') return <Forgot />;

  /*
   * The reflection is behind sign-in. This is presentation: the real protection is server-side —
   * submitting and delivery require a paid purchase the API checks — but there is no reason to show
   * the exercise to someone who has not signed in, so a missing session goes to the sign-in page.
   */
  if (!currentSession()) return redirectToLogin();

  return <App />;
}

function redirectToLogin(): null {
  window.location.replace('/login');
  return null;
}

function redirectHome(): null {
  window.location.replace('/');
  return null;
}

function MissingPlan() {
  return (
    <div className="begin__centered">
      <p>This link is missing something. Please return to the site and choose a plan again.</p>
    </div>
  );
}
